import {
  AdditiveBlending,
  Color,
  DoubleSide,
  NormalBlending,
  ShaderMaterial
} from 'three';
import { sharedUniforms } from '../core/FrameUniforms.js';
import { noiseGLSL } from '../shaders/lib/noise.glsl.js';

const SILHOUETTE_VERTEX = /* glsl */ `
  uniform float uTime;
  uniform float uInstability;
  uniform float uDistortion;

  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;
  varying float vNoise;

  ${noiseGLSL}

  void main() {
    vec4 local = vec4(position, 1.0);
    vec3 localNormal = normal;
    #ifdef USE_INSTANCING
      local = instanceMatrix * local;
      mat3 instanceNormal = mat3(instanceMatrix);
      localNormal /= max(vec3(
        dot(instanceNormal[0], instanceNormal[0]),
        dot(instanceNormal[1], instanceNormal[1]),
        dot(instanceNormal[2], instanceNormal[2])
      ), vec3(1e-8));
      localNormal = instanceNormal * localNormal;
    #endif

    vec4 world = modelMatrix * local;
    vec3 normalW = normalize(mat3(modelMatrix) * localNormal);
    float tear = snoise(world.xyz * 3.1 + vec3(0.0, -uTime * 1.8, uTime * 0.35));
    world.xyz += normalW * tear * uInstability * uDistortion * 0.055;
    world.xz += vec2(
      snoise(world.xyz * 4.7 + uTime),
      snoise(world.zyx * 4.2 - uTime * 0.8)
    ) * uInstability * uDistortion * 0.025;

    vNormalW = normalW;
    vViewW = normalize(cameraPosition - world.xyz);
    vWorld = world.xyz;
    vNoise = tear;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const SILHOUETTE_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uFade;
  uniform float uReveal;
  uniform float uPulse;
  uniform float uInstability;
  uniform float uOpacity;
  uniform float uShaderIntensity;
  uniform float uGlobalGlow;
  uniform vec3 uColorShadow;
  uniform vec3 uColorEdge;
  uniform vec3 uColorCore;

  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;
  varying float vNoise;

  ${noiseGLSL}

  void main() {
    float fresnel = pow(1.0 - abs(dot(normalize(vNormalW), normalize(vViewW))), 2.15);
    float grain = 0.5 + 0.5 * snoise(vWorld * 4.4 + vec3(0.0, uTime * 0.75, 0.0));
    float scan = 0.5 + 0.5 * sin(vWorld.y * 19.0 - uTime * 4.2 + vNoise * 3.0);
    float breakup = mix(0.18, 0.62, uInstability);
    float dissolve = smoothstep(breakup - 0.22, breakup + 0.18, grain + scan * 0.2);
    float alpha = (0.16 + fresnel * 0.68 + grain * 0.16) * dissolve;
    alpha *= uFade * uReveal * uOpacity;
    if (alpha < 0.004) discard;

    vec3 color = mix(uColorShadow, uColorEdge, fresnel + uInstability * grain * 0.35);
    color = mix(color, uColorCore, clamp(uPulse * fresnel + scan * 0.08, 0.0, 1.0));
    color *= uShaderIntensity * uGlobalGlow * (0.72 + uPulse * 0.7);
    gl_FragColor = vec4(color, alpha);
  }
`;

const TRAIL_VERTEX = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorld;

  void main() {
    vUv = uv;
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const TRAIL_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uFade;
  uniform float uProgress;
  uniform float uBidirectional;
  uniform float uOpacity;
  uniform float uTurbulence;
  uniform float uFragmentation;
  uniform float uFlow;
  uniform float uSeed;
  uniform float uShaderIntensity;
  uniform float uGlobalGlow;
  uniform vec3 uColorShadow;
  uniform vec3 uColorEdge;
  uniform vec3 uColorCore;

  varying vec2 vUv;
  varying vec3 vWorld;

  ${noiseGLSL}

  void main() {
    vec2 q = vec2(vUv.x, (vUv.y - 0.5) * 2.0);
    float noise = 0.5 + 0.5 * snoise(vec3(
      q.x * 11.0 - uTime * uFlow,
      q.y * (3.0 + uTurbulence * 2.0),
      uSeed + vWorld.y * 0.7
    ));
    float braidA = abs(q.y - sin(q.x * 31.0 - uTime * uFlow * 2.2 + uSeed) * 0.24 * uTurbulence);
    float braidB = abs(q.y + sin(q.x * 23.0 + uTime * uFlow * 1.7 + uSeed * 1.7) * 0.18);
    float filaments = exp(-braidA * braidA * 11.0) + exp(-braidB * braidB * 18.0) * 0.7;
    float ragged = smoothstep(uFragmentation - 0.26, uFragmentation + 0.24, noise + filaments * 0.24);
    float edge = pow(max(0.0, 1.0 - abs(q.y)), 1.8);

    float forward = smoothstep(uProgress - 0.16, uProgress + 0.02, q.x);
    forward = 1.0 - forward;
    float reverse = smoothstep((1.0 - uProgress) - 0.16, (1.0 - uProgress) + 0.02, 1.0 - q.x);
    reverse = 1.0 - reverse;
    float reach = mix(forward, max(forward, reverse), uBidirectional);
    float headA = exp(-pow((q.x - uProgress) / 0.095, 2.0));
    float headB = exp(-pow((q.x - (1.0 - uProgress)) / 0.095, 2.0)) * uBidirectional;

    float alpha = (filaments * 0.4 + noise * 0.22 + headA + headB) * edge * ragged * reach;
    alpha *= uFade * uOpacity;
    if (alpha < 0.004) discard;

    vec3 color = mix(uColorShadow, uColorEdge, noise + filaments * 0.18);
    color = mix(color, uColorCore, clamp(headA + headB, 0.0, 1.0));
    color *= uShaderIntensity * uGlobalGlow * (0.76 + filaments * 0.55);
    gl_FragColor = vec4(color, alpha);
  }
`;

const DISTORTION_VERTEX = /* glsl */ `
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;

  void main() {
    vec4 local = vec4(position, 1.0);
    vec3 localNormal = normal;
    #ifdef USE_INSTANCING
      local = instanceMatrix * local;
      localNormal = mat3(instanceMatrix) * localNormal;
    #endif
    vec4 world = modelMatrix * local;
    vNormalW = normalize(mat3(modelMatrix) * localNormal);
    vViewW = normalize(cameraPosition - world.xyz);
    vWorld = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const DISTORTION_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uFade;
  uniform float uPulse;
  uniform float uIntensity;
  uniform float uRadiusNoise;
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;

  ${noiseGLSL}

  void main() {
    float fresnel = pow(1.0 - abs(dot(normalize(vNormalW), normalize(vViewW))), 1.5);
    float noise = 0.5 + 0.5 * snoise(vWorld * uRadiusNoise + uTime * 1.4);
    float coverage = clamp((fresnel * 0.8 + noise * 0.3) * uFade, 0.0, 1.0);
    vec2 offset = normalize(vNormalW.xy + vec2(1e-4)) * (0.18 + noise * 0.32);
    gl_FragColor = vec4(offset * 0.5 + 0.5, uIntensity * (0.55 + uPulse), coverage);
  }
`;

function baseUniforms() {
  return sharedUniforms({
    uFade: { value: 1 },
    uReveal: { value: 1 },
    uPulse: { value: 0 },
    uInstability: { value: 0 },
    uDistortion: { value: 1 },
    uOpacity: { value: 1 },
    uProgress: { value: 1 },
    uBidirectional: { value: 0 },
    uTurbulence: { value: 1 },
    uFragmentation: { value: 0.52 },
    uFlow: { value: 2 },
    uSeed: { value: 0 },
    uShaderIntensity: { value: 1 },
    uIntensity: { value: 1 },
    uRadiusNoise: { value: 3 },
    uColorShadow: { value: new Color('#050711') },
    uColorEdge: { value: new Color('#6552c9') },
    uColorCore: { value: new Color('#b9e6ff') }
  });
}

export function createShadowSilhouetteMaterial({ additive = false } = {}) {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: additive ? AdditiveBlending : NormalBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: baseUniforms(),
    vertexShader: SILHOUETTE_VERTEX,
    fragmentShader: SILHOUETTE_FRAGMENT
  });
}

export function createShadowTrailMaterial() {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: AdditiveBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: baseUniforms(),
    vertexShader: TRAIL_VERTEX,
    fragmentShader: TRAIL_FRAGMENT
  });
}

export function createShadowDistortionMaterial() {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: NormalBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: baseUniforms(),
    vertexShader: DISTORTION_VERTEX,
    fragmentShader: DISTORTION_FRAGMENT
  });
}

export function syncShadowMaterial(material, config, state = {}) {
  const u = material.uniforms;
  if (u.uFade) u.uFade.value = state.fade ?? 1;
  if (u.uReveal) u.uReveal.value = state.reveal ?? 1;
  if (u.uPulse) u.uPulse.value = state.pulse ?? 0;
  if (u.uInstability) u.uInstability.value = state.instability ?? 0;
  if (u.uDistortion) u.uDistortion.value = config.distortionIntensity;
  if (u.uOpacity) u.uOpacity.value = (state.opacity ?? 1) * config.opacity;
  if (u.uProgress) u.uProgress.value = state.progress ?? 1;
  if (u.uBidirectional) u.uBidirectional.value = state.bidirectional ?? 0;
  if (u.uTurbulence) u.uTurbulence.value = config.trailTurbulence;
  if (u.uFragmentation) u.uFragmentation.value = config.trailFragmentation;
  if (u.uFlow) u.uFlow.value = config.shaderFlow;
  if (u.uSeed) u.uSeed.value = state.seed ?? 0;
  if (u.uShaderIntensity) u.uShaderIntensity.value = config.shaderIntensity;
  if (u.uIntensity) u.uIntensity.value = config.distortionIntensity * (state.intensity ?? 1);
  if (u.uRadiusNoise) u.uRadiusNoise.value = config.shaderNoiseScale;
  if (u.uColorShadow) u.uColorShadow.value.set(config.colorShadow);
  if (u.uColorEdge) u.uColorEdge.value.set(config.colorEdge);
  if (u.uColorCore) u.uColorCore.value.set(config.colorCore);
}
